import { MembershipChargesApi } from '@trybeapp/sdk.customer'
import {
  ListMembershipCharges,
  ListMembershipChargesRequest,
  MembershipChargesApi as MembershipChargesApiTs,
  ResponseError,
} from '@trybeapp/sdk'
import { useCurrentSiteId } from 'contexts/SiteConfig'
import { useMutation, useQuery, useQueryClient, UseQueryOptions } from 'react-query'
import { apiConfiguration, formatDateForApi } from 'utilities/TrybeApiClient'

const membershipChargesApi = new MembershipChargesApi()
const membershipChargesApiTs = new MembershipChargesApiTs(apiConfiguration)

export const useChargesForMembership = (
  membershipId: string,
  params = {},
  options: UseQueryOptions<ListMembershipCharges, ResponseError> = {}
) => {
  const siteId = useCurrentSiteId()

  return useQuery<ListMembershipCharges, ResponseError>(
    ['chargesForMembership', { membershipId, ...params }],
    async () => membershipChargesApiTs.listMembershipCharges({ siteId, membershipId, ...params }),
    options
  )
}

export const useCharges = (params: Omit<ListMembershipChargesRequest, 'siteId'> = {}) => {
  const siteId = useCurrentSiteId()

  if (params.billingPeriodFromStart !== undefined) {
    params.billingPeriodFromStart = formatDateForApi(params.billingPeriodFromStart)
  }

  if (params.billingPeriodFromEnd !== undefined) {
    params.billingPeriodFromEnd = formatDateForApi(params.billingPeriodFromEnd)
  }

  return useQuery<ListMembershipCharges, ResponseError>(
    ['membershipCharges', { siteId, ...params }],
    () => membershipChargesApiTs.listMembershipCharges({ siteId, ...params })
  )
}

export const useExportCharges = () => {
  const siteId = useCurrentSiteId()

  return useMutation(['exportMembershipCharges'], (params: any) => {
    if (params.billingPeriodFromStart !== undefined) {
      params.billingPeriodFromStart = formatDateForApi(params.billingPeriodFromStart)
    }

    if (params.billingPeriodFromEnd !== undefined) {
      params.billingPeriodFromEnd = formatDateForApi(params.billingPeriodFromEnd)
    }

    return membershipChargesApiTs.queueMembershipCharges({
      siteId,
      ...params,
    })
  })
}

export const useDownloadChargeReceipt = () =>
  useMutation((chargeId: string) => membershipChargesApi.getReceipt(chargeId))

export const useRefundMembershipCharge = () => {
  const queryClient = useQueryClient()

  return useMutation(
    ({ chargeId, amount }: any) =>
      membershipChargesApi.refundMembershipCharge(chargeId, {
        refundMembershipChargeRequest: {
          amount,
        },
      }),
    {
      onSuccess: (e: any) => {
        const membershipId = e.data?.membership.id
        queryClient.invalidateQueries(['chargesForMembership', { membershipId }])
      },
    }
  )
}
